import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ActivityIcon, ArrowLeftIcon, CheckIcon, ClockIcon, Loader2Icon, RefreshCwIcon, SearchXIcon, Trash2Icon } from 'lucide-react';
import { useAccount } from '../../contexts/AccountContext';
import { Panel } from '../../components/ui/Panel';
import { PanelHeader } from '../../components/ui/PanelHeader';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { SelectInput } from '../../components/ui/SelectInput';
import { TextInput } from '../../components/ui/TextInput';
import { Toggle } from '../../components/ui/Toggle';
import { StatusBadge } from '../../components/StatusBadge';
import { CertificateCard } from '../../components/CertificateCard';
import { UptimeChart } from '../../components/UptimeChart';
import { EmptyState } from '../../components/EmptyState';
import { overallStatus, uptimeStatus } from '../../utils/health';
import { formatAvailability, formatDateTime, formatDuration, formatInterval, relativeTime } from '../../utils/format';
const ranges = [{
  key: '24h',
  label: '24 hours',
  points: 24
}, {
  key: '7d',
  label: '7 days',
  points: 48
}, {
  key: '30d',
  label: '30 days',
  points: 48
}] as const;
export function MonitorDetail() {
  const {
    monitorId
  } = useParams();
  const navigate = useNavigate();
  const {
    monitors,
    recipients,
    plan,
    servicesUsed,
    serviceLimit,
    toggleCheck,
    updateMonitor,
    touchMonitor,
    removeMonitor
  } = useAccount();
  const monitor = monitors.find(item => item.id === monitorId);
  const [range, setRange] = useState<(typeof ranges)[number]['key']>('24h');
  const [checking, setChecking] = useState(false);
  const [checked, setChecked] = useState(false);
  if (!monitor) {
    return <Panel data-id="element-939" data-mp="173e2tfy0xb9r">
        <EmptyState icon={SearchXIcon} title="That monitor is gone" body="It may have been removed, or the link is out of date." action={<Button to="/app" data-id="element-941" data-mp="13l6y4j27egu2">Back to monitors</Button>} data-id="element-940" data-mp="16zqcepnvl4z9" />
      </Panel>;
  }
  const atLimit = servicesUsed >= serviceLimit;
  const availability = range === '24h' ? monitor.availability24h : range === '7d' ? monitor.availability7d : monitor.availability30d;
  const activeRange = ranges.find(item => item.key === range) ?? ranges[0];
  const chartValues = monitor.responseTimes.slice(-activeRange.points);
  const liveTimes = monitor.responseTimes.filter(value => value > 0);
  const average = liveTimes.length > 0 ? Math.round(liveTimes.reduce((sum, v) => sum + v, 0) / liveTimes.length) : 0;
  function runCheck() {
    setChecking(true);
    setChecked(false);
    window.setTimeout(() => {
      touchMonitor(monitor.id);
      setChecking(false);
      setChecked(true);
      window.setTimeout(() => setChecked(false), 4000);
    }, 1200);
  }
  const windowOptions = (() => {
    if (plan.warningWindowFixed) {
      return [{
        value: String(plan.warningWindowFixed),
        label: `${plan.warningWindowFixed} days (fixed on ${plan.name})`
      }];
    }
    const [min, max] = plan.warningWindowRange ?? [1, 60];
    return [7, 14, 21, 30, 45, 60, 90].filter(days => days >= min && days <= max).map(days => ({
      value: String(days),
      label: `${days} days`
    }));
  })();
  const pingOptions = [30, 15, 5, 1].map(minutes => ({
    value: String(minutes),
    label: plan.pingOptions.includes(minutes) ? formatInterval(minutes) : `${formatInterval(minutes)} — Pro only`,
    disabled: !plan.pingOptions.includes(minutes)
  }));
  return <div className="space-y-5" data-id="element-942" data-mp="axf9fvlnud8j">
      <Link to="/app" className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ink-muted transition-colors duration-150 ease-snap hover:text-ink" data-id="element-943" data-mp="aygnm8xz3fuh">
        <ArrowLeftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-944" data-mp="jylsocfp225h" />
        All monitors
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-945" data-mp="2ef3t6oze4c9p">
        <div className="min-w-0" data-id="element-946" data-mp="2jdf12u64kcpa">
          <div className="flex flex-wrap items-center gap-2.5" data-id="element-947" data-mp="2jcdebsfqy1rq">
            <h1 className="text-xl font-semibold tracking-tight text-ink" data-id="element-948" data-mp="2o43wgkkxdttp">
              {monitor.domain}
            </h1>
            <StatusBadge status={overallStatus(monitor)} size="md" data-id="element-949" data-mp="2s5ysb9k37f03" />
          </div>
          <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-ink-muted" data-id="element-950" data-mp="1vfpww0e6upg1">
            <span data-id="element-951" data-mp="20e1566ou4rw5">{monitor.label}</span>
            <span aria-hidden="true" data-id="element-952" data-mp="20f2pz2vnksr2">·</span>
            <span className="flex items-center gap-1.5" data-id="element-953" data-mp="24ge86nc76bp8">
              <ClockIcon className="h-3 w-3" aria-hidden="true" data-id="element-954" data-mp="24etdmxpvusrd" />
              Checked {relativeTime(monitor.lastCheckedAt)}
            </span>
            <span aria-hidden="true" data-id="element-955" data-mp="3fvjncqtim2pq">·</span>
            <span data-id="element-956" data-mp="3fursxjbnmb11">next {relativeTime(monitor.nextCheckAt)}</span>
          </p>
        </div>

        <div className="flex items-center gap-2" data-id="element-957" data-mp="3kt30toie2bgm">
          {checked && <span className="flex items-center gap-1.5 text-[13px] font-medium text-healthy-ink" data-id="element-958" data-mp="3uyks4ngsqh2o">
              <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-959" data-mp="3pyyewe0yx0tv" />
              Up to date
            </span>}
          <Button variant="secondary" onClick={runCheck} disabled={checking} data-id="element-960" data-mp="3l76xaszsi5fv">
            {checking ? <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-961" data-mp="3h6eo1zqx40jl" /> : <RefreshCwIcon className="h-4 w-4" aria-hidden="true" data-id="element-962" data-mp="3c9egtlic0rqw" />}
            {checking ? 'Checking' : 'Check now'}
          </Button>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-2" data-id="element-963" data-mp="32aozpuy9fbbp">
        <CertificateCard monitor={monitor} data-id="element-964" data-mp="32bgu52g4f30e" />

        <Panel as="section" data-id="element-965" data-mp="2y93r4lsrdj7b">
          <PanelHeader title="Uptime" description={monitor.uptimeEnabled ? `${formatInterval(monitor.pingIntervalMinutes)} · ${monitor.method} expecting ${monitor.expectedStatus}` : undefined} action={<StatusBadge status={uptimeStatus(monitor)} size="md" data-id="element-967" data-mp="2pxlbbbl3fltw" />} data-id="element-966" data-mp="2y9vbt1kthwit" />

          {!monitor.uptimeEnabled ? <div className="flex items-start gap-3 px-5 py-6" data-id="element-968" data-mp="2gz9lvcvyqu31">
              <ActivityIcon className="mt-0.5 h-5 w-5 shrink-0 text-paused" aria-hidden="true" data-id="element-969" data-mp="2gwn4klj9t07m" />
              <p className="text-sm text-ink-muted" data-id="element-970" data-mp="38pntirddva69">
                Uptime is switched off for this domain. Nothing is being pinged
                and no downtime alerts will be sent.
              </p>
            </div> : <div className="px-5 py-5" data-id="element-971" data-mp="2zopqabarr6oq">
              <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-972" data-mp="34o28ujulxrli">
                <div data-id="element-973" data-mp="34q59dxtdz34h">
                  <span className="tabular text-4xl font-semibold leading-none text-ink" data-id="element-974" data-mp="1oac1934a4l74">
                    {formatAvailability(availability)}
                  </span>
                  <p className="mt-2 text-[13px] text-ink-muted" data-id="element-975" data-mp="1t72ivuvqfoun">
                    available over {activeRange.label} · {average}ms average
                  </p>
                </div>
                <div className="inline-flex items-center gap-1 rounded-control border border-line-strong bg-canvas p-1" role="group" aria-label="Time range" data-id="element-976" data-mp="1gq9vn2ufxfe6">
                  {ranges.map(option => <button key={option.key} type="button" onClick={() => setRange(option.key)} aria-pressed={range === option.key} className={`rounded-[5px] px-2.5 py-1 text-xs font-medium transition-colors duration-150 ease-snap ${range === option.key ? 'bg-surface text-ink shadow-card' : 'text-ink-muted hover:text-ink'}`} data-id="element-977" data-mp="122f1rj4p54zi">
                      {option.key}
                    </button>)}
                </div>
              </div>

              <div className="mt-5" data-id="element-978" data-mp="3twfanqih4by7">
                <UptimeChart values={chartValues} label={`Response times for ${monitor.domain} over ${activeRange.label}`} data-id="element-979" data-mp="3txgvgmpakct4" />
                <p className="mt-2 text-xs text-ink-soft" data-id="element-980" data-mp="7qs44q0wia5u">
                  Red bands are failed checks. The line is response time in
                  milliseconds.
                </p>
              </div>

              <div className="mt-6 border-t border-line pt-4" data-id="element-981" data-mp="gp42xfbx8e9d">
                <h4 className="text-[13px] font-semibold text-ink" data-id="element-982" data-mp="lgvlb2kxbdof">
                  Incidents
                </h4>
                {monitor.incidents.length === 0 ? <p className="mt-2 text-[13px] text-ink-muted" data-id="element-983" data-mp="qicioo478i09">
                    No downtime recorded in the last {plan.historyLabel}.
                  </p> : <ul className="mt-3 space-y-3" data-id="element-984" data-mp="zf3ohs8jzb81">
                    {monitor.incidents.map(incident => <li key={incident.id} className="flex flex-wrap items-start justify-between gap-3 border-b border-line pb-3 last:border-b-0 last:pb-0" data-id="element-985" data-mp="2jww2tztbleq7">
                        <div className="min-w-0" data-id="element-986" data-mp="25zza1stn5n6o">
                          <p className="text-[13px] font-medium text-ink" data-id="element-987" data-mp="261aiw71y1l4n">
                            {incident.reason}
                          </p>
                          <p className="mt-0.5 text-xs text-ink-soft" data-id="element-988" data-mp="2ynv6cwfatd2x">
                            {formatDateTime(incident.startedAt)}
                            {incident.statusCode ? ` · HTTP ${incident.statusCode}` : ''}
                          </p>
                        </div>
                        <span className={`tabular shrink-0 text-[13px] font-medium ${incident.endedAt ? 'text-ink-muted' : 'text-critical-ink'}`} data-id="element-989" data-mp="3mf0m7bl8o5d8">
                          {formatDuration(incident.durationMinutes)}
                        </span>
                      </li>)}
                  </ul>}
              </div>
            </div>}
        </Panel>
      </div>

      <Panel as="section" data-id="element-990" data-mp="3n95l10e7v9ec">
        <PanelHeader title="This monitor's settings" description="Changes take effect on the next scheduled check." level={2} data-id="element-991" data-mp="3n8donncsp7n0" />

        <div className="grid gap-6 px-5 py-5 lg:grid-cols-2" data-id="element-992" data-mp="tuds0n14ala">
          <div className="space-y-5" data-id="element-993" data-mp="3s1glq5gs6z7y">
            <div className="rounded-card border border-line bg-canvas px-4 py-4" data-id="element-994" data-mp="3s2hxkc5mfpqq">
              <div className="flex items-start justify-between gap-4" data-id="element-995" data-mp="9usk4pkxwu71">
                <div data-id="element-996" data-mp="5u8l1ggt3gi9">
                  <p className="text-[13px] font-medium text-ink" data-id="element-997" data-mp="5urt85gnmqj3">
                    Certificate expiry
                  </p>
                  <p className="mt-0.5 text-xs text-ink-muted" data-id="element-998" data-mp="oisjig5inuz9">
                    Inspected once a day. Uses 1 service.
                  </p>
                </div>
                <Toggle checked={monitor.sslEnabled} onChange={() => toggleCheck(monitor.id, 'ssl')} disabled={!monitor.sslEnabled && atLimit} label="Monitor certificate expiry" data-id="element-999" data-mp="fi471pvru2oh" />
              </div>
              {!monitor.sslEnabled && atLimit && <p className="mt-2.5 text-xs text-warn-ink" data-id="element-1000" data-mp="ajstp569pa1i">
                  All {serviceLimit} services on {plan.name} are in use.{' '}
                  <Link to="/app/billing" className="font-medium underline decoration-1 underline-offset-2" data-id="element-1001" data-mp="sf0ff4wt6gt">
                    Move up a plan
                  </Link>{' '}
                  to switch this on.
                </p>}
            </div>

            <div className="rounded-card border border-line bg-canvas px-4 py-4" data-id="element-1002" data-mp="1thflvouo6xzq">
              <div className="flex items-start justify-between gap-4" data-id="element-1003" data-mp="1oqpfq9ef6r3z">
                <div data-id="element-1004" data-mp="1oomib4axtvp8">
                  <p className="text-[13px] font-medium text-ink" data-id="element-1005" data-mp="1kom49krr3mip">Uptime</p>
                  <p className="mt-0.5 text-xs text-ink-muted" data-id="element-1006" data-mp="1kmhqmb41v958">
                    Pinged on your interval. Uses 1 service.
                  </p>
                </div>
                <Toggle checked={monitor.uptimeEnabled} onChange={() => toggleCheck(monitor.id, 'uptime')} disabled={!monitor.uptimeEnabled && atLimit} label="Monitor uptime" data-id="element-1007" data-mp="2cfi5tp8d24qo" />
              </div>
              {!monitor.uptimeEnabled && atLimit && <p className="mt-2.5 text-xs text-warn-ink" data-id="element-1008" data-mp="s06rl4w3dgaj">
                  All {serviceLimit} services on {plan.name} are in use.{' '}
                  <Link to="/app/billing" className="font-medium underline decoration-1 underline-offset-2" data-id="element-1009" data-mp="n029jafjcdi7">
                    Move up a plan
                  </Link>{' '}
                  to switch this on.
                </p>}
            </div>

            <Field label="Warning window" htmlFor="warning-window" hint="How close to expiry before we start warning you." data-id="element-1010" data-mp="mtryb6f8ygmy">
              <SelectInput id="warning-window" value={String(monitor.warningWindowDays)} disabled={Boolean(plan.warningWindowFixed)} onChange={event => updateMonitor(monitor.id, {
              warningWindowDays: Number(event.target.value)
            })} options={windowOptions} data-id="element-1011" data-mp="vmmulamql20l" />
            </Field>

            <div data-id="element-1012" data-mp="37fk11hrsib59">
              <p className="text-[13px] font-medium text-ink" data-id="element-1013" data-mp="3cc0la0vvc5d2">
                Reminder thresholds
              </p>
              <p className="mt-0.5 text-xs text-ink-soft" data-id="element-1014" data-mp="16n8esfq5xl0q">
                Days before expiry that trigger an email.
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5" data-id="element-1015" data-mp="11of22pip9y1m">
                {[30, 21, 14, 7, 3, 1].map(days => {
                const active = monitor.reminderCadence.includes(days);
                return <button key={days} type="button" aria-pressed={active} onClick={() => updateMonitor(monitor.id, {
                  reminderCadence: active ? monitor.reminderCadence.filter(d => d !== days) : [...monitor.reminderCadence, days].sort((a, b) => b - a)
                })} className={`tabular rounded-control border px-2.5 py-1.5 text-xs font-medium transition-colors duration-150 ease-snap ${active ? 'border-brand bg-brand-soft text-brand-ink' : 'border-line-strong text-ink-muted hover:border-ink-soft hover:text-ink'}`} data-id="element-1016" data-mp="1anilxvpoyhh6">
                      {days}d
                    </button>;
              })}
              </div>
            </div>
          </div>

          <div className="space-y-5" data-id="element-1017" data-mp="2ujy31ucki4us">
            <Field label="Ping interval" htmlFor="ping-interval" data-id="element-1018" data-mp="2ynnq2fdlgedp">
              <SelectInput id="ping-interval" value={String(monitor.pingIntervalMinutes)} onChange={event => updateMonitor(monitor.id, {
              pingIntervalMinutes: Number(event.target.value)
            })} options={pingOptions} data-id="element-1019" data-mp="2knlax0wo7z25" />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2" data-id="element-1020" data-mp="1e568bary8lib">
              <Field label="Request method" htmlFor="method" data-id="element-1021" data-mp="u4d3txxs7gqm">
                <SelectInput id="method" value={monitor.method} onChange={event => updateMonitor(monitor.id, {
                method: event.target.value as typeof monitor.method
              })} options={[{
                value: 'GET',
                label: 'GET'
              }, {
                value: 'HEAD',
                label: 'HEAD'
              }, {
                value: 'POST',
                label: 'POST'
              }]} data-id="element-1022" data-mp="q42wohy96ka6" />
              </Field>
              <Field label="Expected status" htmlFor="expected-status" data-id="element-1023" data-mp="2es0ldrule2d">
                <TextInput id="expected-status" inputMode="numeric" className="tabular" value={String(monitor.expectedStatus)} onChange={event => updateMonitor(monitor.id, {
                expectedStatus: Number(event.target.value.replace(/\D/g, '') || 0)
              })} data-id="element-1024" data-mp="3tll44oa45pa2" />
              </Field>
            </div>

            <div className="grid gap-4 sm:grid-cols-2" data-id="element-1025" data-mp="2930qausmp3wc">
              <Field label="Timeout" htmlFor="timeout" hint="Seconds" data-id="element-1026" data-mp="2e23mrqujm1sp">
                <TextInput id="timeout" inputMode="numeric" className="tabular" value={String(monitor.timeoutSeconds)} onChange={event => updateMonitor(monitor.id, {
                timeoutSeconds: Number(event.target.value.replace(/\D/g, '') || 0)
              })} data-id="element-1027" data-mp="2e3eug1r3ztp3" />
              </Field>
              <Field label="Failures before alerting" htmlFor="threshold" hint="Consecutive" data-id="element-1028" data-mp="31tiww5sk9nei">
                <SelectInput id="threshold" value={String(monitor.failureThreshold)} onChange={event => updateMonitor(monitor.id, {
                failureThreshold: Number(event.target.value)
              })} options={[1, 2, 3, 5].map(count => ({
                value: String(count),
                label: `${count} ${count === 1 ? 'check' : 'checks'}`
              }))} data-id="element-1029" data-mp="3rv37q1mv2rrq" />
              </Field>
            </div>

            <fieldset data-id="element-1030" data-mp="oduuxw6zqi3v">
              <legend className="text-[13px] font-medium text-ink" data-id="element-1031" data-mp="oc9x9xpdqj1s">
                Who gets alerted about this domain
              </legend>
              <div className="mt-2 space-y-2" data-id="element-1032" data-mp="2q4y6qosma2q0">
                {recipients.map(recipient => <label key={recipient.id} className="flex items-center gap-2.5 rounded-control border border-line bg-canvas px-3 py-2" data-id="element-1033" data-mp="2l5v0x24tbsgz">
                    <input type="checkbox" className="h-3.5 w-3.5 rounded border-line-strong text-brand focus:ring-brand" checked={monitor.recipientIds.includes(recipient.id)} disabled={!recipient.verified} onChange={() => updateMonitor(monitor.id, {
                  recipientIds: monitor.recipientIds.includes(recipient.id) ? monitor.recipientIds.filter(id => id !== recipient.id) : [...monitor.recipientIds, recipient.id]
                })} data-id="element-1034" data-mp="15mxgarjjwxxp" />
                    <span className="min-w-0 flex-1 truncate text-[13px] text-ink" data-id="element-1035" data-mp="27cssul8efnvu">
                      {recipient.email}
                    </span>
                    {!recipient.verified && <span className="shrink-0 text-[11px] text-warn-ink" data-id="element-1036" data-mp="3fnp5wfmq30su">
                        Not confirmed
                      </span>}
                  </label>)}
              </div>
            </fieldset>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-5 py-4" data-id="element-1037" data-mp="3tmx87he6mp19">
          <p className="text-xs text-ink-soft" data-id="element-1038" data-mp="2gdn99emtip8">
            Removing a monitor deletes its history and frees its services.
          </p>
          <Button variant="danger" size="sm" onClick={() => {
          removeMonitor(monitor.id);
          navigate('/app');
        }} data-id="element-1039" data-mp="3kl77tm2bofrc">
            <Trash2Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1040" data-mp="2961y3y3rxgy8" />
            Remove {monitor.domain}
          </Button>
        </div>
      </Panel>
    </div>;
}